import {
  Box,
  Button,
  Chip,
  Divider,
  Drawer,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Toolbar,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import {
  ArrowBack,
  ArrowDropDown,
  PsychologyAlt,
  HomeOutlined,
  Menu as MenuIcon,
  StorageOutlined as StorageIcon,
  HubOutlined as IntegrationsNavIcon,
  Settings,
  Person,
  LogoutSharp,
} from "@mui/icons-material";
import { useState } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import {
  CHCM_LOGO_MARK_WHITE_URL,
  TOP_BAR_LOGO_HEIGHT_PX,
  SHELL_TOP_BAR_PX,
  SHELL_SIDEBAR_WIDTH_PX,
  SHELL_NAV_ROW_HEIGHT_PX,
  SHELL_NAV_GAP_PX,
  SHELL_NAV_ROW_INNER_SPACING,
} from "config";
import { useAppSelector } from "features/store";

interface ClientViewHeaderProps {
  onBackToUserManagement: () => void;
  children?: React.ReactNode;
}

export default function ClientViewHeader({
  onBackToUserManagement,
  children,
}: ClientViewHeaderProps) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const { userId } = useParams<{ userId: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const clientView = useAppSelector((state) => state.clientView);
  const platformProfile = useAppSelector(
    (state) => state.auth.user?.platform_profile,
  );
  const showDisabledManagementOptions = !!(
    platformProfile?.is_superuser || platformProfile?.is_calibrate_manager
  );

  const basePath = `/client-view/${userId ?? ""}`;
  const storagePath = `${basePath}/data-storage`;
  const isLandingPath =
    location.pathname === basePath || location.pathname === basePath + "/";
  const isDataStoragePath = location.pathname === storagePath;
  const isActive = (path: string) => {
    if (path === basePath || path === basePath + "/")
      return location.pathname === path || location.pathname === path + "/";
    return location.pathname.startsWith(path);
  };

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const menuOpen = Boolean(anchorEl);

  const closeDrawerAndNavigate = (path: string) => {
    setDrawerOpen(false);
    navigate(path);
  };

  const navRowSx = (active: boolean, hovered: boolean) => {
    const shActive = theme.palette.calibrate.navRowShadowActive;
    const shHover = theme.palette.calibrate.navRowShadowHover;
    const rowShadow = active ? shActive : hovered ? shHover : "none";
    return {
      minHeight: SHELL_NAV_ROW_HEIGHT_PX,
      height: SHELL_NAV_ROW_HEIGHT_PX,
      p: 0,
      boxSizing: "border-box" as const,
      display: "flex",
      alignItems: "stretch",
      justifyContent: "flex-start",
      cursor: "pointer",
      border: "none",
      width: "100%",
      textAlign: "left" as const,
      fontFamily: theme.typography.calibrateShell.fontFamily,
      fontSize: theme.typography.calibrateShell.fontSize,
      fontWeight: active ? 600 : 500,
      color: theme.palette.calibrate.iconOnTeal,
      bgcolor: active ? theme.palette.calibrate.navActive : "transparent",
      borderRadius: 0,
      transition: "background-color 0.15s ease, box-shadow 0.15s ease",
      boxShadow: rowShadow,
      "&:hover": {
        bgcolor: active
          ? theme.palette.calibrate.navActive
          : theme.palette.calibrate.navHover,
        boxShadow: active ? shActive : shHover,
      },
    };
  };

  const navIconSlotSx = {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: 24,
    height: 24,
    flexShrink: 0,
    lineHeight: 0,
  };

  const navRowInnerSx = {
    display: "flex",
    alignItems: "center",
    gap: 1.5,
    flex: 1,
    minWidth: 0,
    minHeight: 0,
    height: "100%",
    px: SHELL_NAV_ROW_INNER_SPACING,
    boxSizing: "border-box" as const,
    width: "100%",
  };

  const NavRow = ({
    icon,
    label,
    active,
    onClick,
    disabled,
  }: {
    icon: React.ReactNode;
    label: string;
    active: boolean;
    onClick: () => void;
    disabled?: boolean;
  }) => {
    const [hover, setHover] = useState(false);
    return (
      <Box
        component="button"
        type="button"
        disabled={disabled}
        onClick={onClick}
        onMouseEnter={() => setHover(true)}
        onMouseLeave={() => setHover(false)}
        sx={{
          ...navRowSx(active, hover),
          opacity: disabled ? 0.5 : 1,
          cursor: disabled ? "not-allowed" : "pointer",
        }}
      >
        <Box sx={navRowInnerSx}>
          <Box sx={navIconSlotSx}>{icon}</Box>
          <Typography
            component="span"
            sx={{
              color: "calibrate.navText",
              font: "inherit",
              fontWeight: "inherit",
              lineHeight: theme.typography.calibrateShell.lineHeight,
              display: "flex",
              alignItems: "center",
            }}
          >
            {label}
          </Typography>
        </Box>
      </Box>
    );
  };

  if (!clientView.active || !userId) return null;

  const sidebarNav = (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: `${SHELL_NAV_GAP_PX}px`,
        px: 0,
        pb: 2,
      }}
    >
      <NavRow
        icon={<HomeOutlined sx={{ fontSize: 22, color: "inherit" }} />}
        label="Home"
        active={isLandingPath}
        onClick={() => navigate(basePath)}
      />
      {clientView.isDataStorageUser && (
        <NavRow
          icon={<StorageIcon sx={{ fontSize: 22, color: "inherit" }} />}
          label="Data Storage"
          active={isDataStoragePath}
          onClick={() => navigate(storagePath)}
        />
      )}
      {clientView.isIntegrationUser && (
        <NavRow
          icon={<IntegrationsNavIcon sx={{ fontSize: 22, color: "inherit" }} />}
          label="Integrations"
          active={isActive(`${basePath}/integrations`)}
          onClick={() => navigate(`${basePath}/integrations`)}
        />
      )}
      {clientView.isChatBotAllowed && (
        <NavRow
          icon={<PsychologyAlt sx={{ fontSize: 22, color: "inherit" }} />}
          label="AI Reporting"
          active={isActive(`${basePath}/chat`)}
          onClick={() => navigate(`${basePath}/chat`)}
        />
      )}
    </Box>
  );

  const drawerContent = (
    <Box
      role="presentation"
      sx={{
        width: "100%",
        minHeight: "100%",
        height: "100%",
        boxSizing: "border-box",
        bgcolor: "primary.main",
        pt: 2,
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Box
        sx={{
          flex: "1 1 auto",
          minHeight: 0,
          overflowY: "auto",
          bgcolor: "primary.main",
        }}
      >
        {sidebarNav}
      </Box>
      <Divider
        sx={{ borderColor: "rgba(255,255,255,0.2)", my: 1, flexShrink: 0 }}
      />
      <Box sx={{ px: 0, pb: 2, flexShrink: 0, bgcolor: "primary.main" }}>
        <NavRow
          icon={<Settings sx={{ fontSize: 22, color: "inherit" }} />}
          label="Account settings"
          active={isActive(`${basePath}/account-settings`)}
          onClick={() => closeDrawerAndNavigate(`${basePath}/account-settings`)}
        />
      </Box>
    </Box>
  );

  const welcomeLabel = clientView.displayName?.trim() || "Client";

  const userMenuPaperSx = {
    mt: 1.25,
    minWidth: 220,
    borderRadius: 1,
    border: "1px solid rgba(0,0,0,0.2)",
    boxShadow: "0px 8px 24px rgba(0,0,0,0.12)",
  };

  return (
    <>
      <Box
        sx={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          minHeight: 0,
          overflow: "hidden",
        }}
      >
        <Box
          component="header"
          sx={{
            width: "100%",
            height: SHELL_TOP_BAR_PX,
            minHeight: SHELL_TOP_BAR_PX,
            maxHeight: SHELL_TOP_BAR_PX,
            flexShrink: 0,
            boxSizing: "border-box",
            bgcolor: "primary.main",
            boxShadow: theme.palette.calibrate.topBarShadow,
            display: "flex",
            alignItems: "center",
            zIndex: theme.zIndex.appBar,
          }}
        >
          <Toolbar
            disableGutters
            sx={{
              minHeight: SHELL_TOP_BAR_PX,
              maxHeight: SHELL_TOP_BAR_PX,
              width: "100%",
              px: 0,
              display: "flex",
              alignItems: "center",
              gap: 1,
              boxSizing: "border-box",
            }}
          >
            {isMobile && (
              <IconButton
                edge="start"
                sx={{ color: "#fff", ml: 0.5 }}
                aria-label="open menu"
                onClick={() => setDrawerOpen(true)}
              >
                <MenuIcon />
              </IconButton>
            )}
            <Link to={basePath} style={{ display: "flex", flexShrink: 0 }}>
              <Box
                component="img"
                src={CHCM_LOGO_MARK_WHITE_URL}
                alt="Calibrate HCM"
                sx={{
                  height: TOP_BAR_LOGO_HEIGHT_PX,
                  minHeight: TOP_BAR_LOGO_HEIGHT_PX,
                  maxHeight: TOP_BAR_LOGO_HEIGHT_PX,
                  width: "auto",
                  ml: "10px",
                  display: "block",
                  objectFit: "contain",
                  flexShrink: 0,
                }}
              />
            </Link>
            <Button
              size="small"
              startIcon={<ArrowBack sx={{ color: "#fff" }} />}
              onClick={onBackToUserManagement}
              sx={{
                textTransform: "none",
                ...theme.typography.calibrateShell,
                fontWeight: 600,
                color: "#fff",
                flexShrink: 0,
              }}
            >
              {isMobile ? "Back" : "Back to User Management"}
            </Button>
            <Tooltip title="Read-only view as this client. You are not acting on their behalf.">
              <Chip
                label={`Client View · ${clientView.displayName || "this client"}`}
                size="small"
                sx={{
                  height: 28,
                  fontWeight: 600,
                  fontFamily: theme.typography.calibrateShell.fontFamily,
                  letterSpacing: theme.typography.calibrateShell.letterSpacing,
                  bgcolor: "rgba(255,255,255,0.2)",
                  color: "#fff",
                  "& .MuiChip-label": { px: 1.25 },
                  flexShrink: 0,
                  maxWidth: { xs: 120, sm: 280 },
                }}
              />
            </Tooltip>
            <Box sx={{ flexGrow: 1 }} />
            {showDisabledManagementOptions && (
              <Tooltip
                title="Management options available for your administrator account cannot be opened in Client View. Exit Client View to use User Management or SFTP Management wherever your role allows."
                placement="bottom-end"
              >
                <span
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    maxWidth: "100%",
                  }}
                >
                  <Button
                    endIcon={
                      <ArrowDropDown sx={{ color: "rgba(255,255,255,0.75)" }} />
                    }
                    disabled
                    sx={{
                      color: "rgba(255,255,255,0.9)",
                      textTransform: "none",
                      ...theme.typography.calibrateShell,
                      display: "inline-flex",
                      alignItems: "center",
                      maxWidth: { xs: 160, sm: "none" },
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                      mr: { xs: 0.5, md: 1 },
                      "&.Mui-disabled": {
                        color: "rgba(255,255,255,0.88)",
                        WebkitTextFillColor: "rgba(255,255,255,0.88)",
                      },
                      "& .MuiButton-label": {
                        display: "inline-flex",
                        alignItems: "center",
                        gap: "2px",
                      },
                      "& .MuiButton-endIcon": {
                        opacity: 0.75,
                      },
                    }}
                  >
                    <Box
                      component="span"
                      sx={{ display: { xs: "none", sm: "inline" } }}
                    >
                      Management Options
                    </Box>
                    <Box
                      component="span"
                      sx={{ display: { xs: "inline", sm: "none" } }}
                    >
                      Manage
                    </Box>
                  </Button>
                </span>
              </Tooltip>
            )}
            {!showDisabledManagementOptions && (
              <Typography
                sx={{
                  color: "#fff",
                  ...theme.typography.calibrateShell,
                  fontWeight: 500,
                  display: { xs: "none", md: "block" },
                  mr: 1,
                }}
              >
                Welcome, {welcomeLabel}!
              </Typography>
            )}
            <IconButton
              onClick={(e) => setAnchorEl(e.currentTarget)}
              size="small"
              sx={{
                mr: "10px",
                p: 0,
                width: 40,
                height: 40,
                borderRadius: "50%",
                border: "none",
                boxShadow: "none",
                bgcolor: "calibrate.iconOnTeal",
                "&:hover": { bgcolor: "#d4eef5" },
              }}
            >
              <Person sx={{ fontSize: 26, color: "primary.main" }} />
            </IconButton>
            <Menu
              anchorEl={anchorEl}
              open={menuOpen}
              onClose={() => setAnchorEl(null)}
              anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
              transformOrigin={{ vertical: "top", horizontal: "right" }}
              PaperProps={{ sx: userMenuPaperSx }}
            >
              <MenuItem
                onClick={() => {
                  setAnchorEl(null);
                  navigate(`${basePath}/account-settings`);
                }}
                sx={{
                  py: 1,
                  "&:hover": { bgcolor: "rgba(49, 129, 159, 0.1)" },
                }}
              >
                <ListItemIcon sx={{ minWidth: 36 }}>
                  <Settings sx={{ fontSize: 20, color: "text.primary" }} />
                </ListItemIcon>
                <ListItemText
                  primary="Account Settings"
                  primaryTypographyProps={{
                    fontSize: 14,
                    fontFamily: theme.typography.fontFamily,
                  }}
                />
              </MenuItem>
              <MenuItem
                onClick={() => {
                  setAnchorEl(null);
                  onBackToUserManagement();
                }}
                sx={{
                  py: 1,
                  "&:hover": { bgcolor: "rgba(255, 218, 218, 0.8)" },
                }}
              >
                <ListItemIcon sx={{ minWidth: 36 }}>
                  <LogoutSharp sx={{ fontSize: 20, color: "error.main" }} />
                </ListItemIcon>
                <ListItemText
                  primary="Exit client view"
                  primaryTypographyProps={{
                    fontSize: 14,
                    fontWeight: 600,
                    color: "error.main",
                  }}
                />
              </MenuItem>
            </Menu>
          </Toolbar>
        </Box>

        <Box
          sx={{
            flex: 1,
            display: "flex",
            flexDirection: "row",
            alignItems: "stretch",
            minHeight: 0,
            minWidth: 0,
            overflow: "hidden",
          }}
        >
          {!isMobile && (
            <Box
              component="nav"
              aria-label="Client view navigation"
              sx={{
                width: SHELL_SIDEBAR_WIDTH_PX,
                minWidth: SHELL_SIDEBAR_WIDTH_PX,
                maxWidth: SHELL_SIDEBAR_WIDTH_PX,
                flexShrink: 0,
                alignSelf: "stretch",
                bgcolor: "primary.main",
                display: "flex",
                flexDirection: "column",
                minHeight: 0,
                overflow: "hidden",
                borderRight: "1px solid rgba(0,0,0,0.06)",
              }}
            >
              <Box
                sx={{
                  flex: 1,
                  minHeight: 0,
                  overflow: "hidden",
                  overflowY: "auto",
                  pt: 2,
                  overscrollBehavior: "contain",
                  bgcolor: "primary.main",
                }}
              >
                {sidebarNav}
              </Box>
            </Box>
          )}
          <Box
            sx={{
              flex: 1,
              minWidth: 0,
              minHeight: 0,
              display: "flex",
              flexDirection: "column",
              overflow: "hidden",
              bgcolor: "background.default",
            }}
          >
            {children}
          </Box>
        </Box>
      </Box>

      <Drawer
        anchor="left"
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        sx={{
          "& .MuiDrawer-paper": {
            width: 280,
            boxSizing: "border-box",
            bgcolor: "primary.main",
            height: "100%",
            minHeight: "100%",
            backgroundImage: "none",
            overflow: "hidden",
          },
        }}
      >
        {drawerContent}
      </Drawer>
    </>
  );
}
